import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import * as SelectPrimitive from "@radix-ui/react-select";
import clsx from "clsx";
import React from "react";

export const Select = SelectPrimitive.Root;

export function SelectTrigger({ className, children, rounded, ...props }: React.ComponentProps<typeof SelectPrimitive.Trigger> & { rounded?: boolean }) {
  return (
    <SelectPrimitive.Trigger
      className={clsx(
        "tw-relative tw-w-full tw-cursor-default tw-bg-white tw-pl-2.5 tw-text-left",
        "tw-border tw-border-solid max-sm:tw-text-base",
        "tw-outline-none focus-visible:tw-ring-1",
        // Same redesign-v1 opt-in as `Input` (`rounded` implies inside `[data-redesign-v1]`).
        rounded ? "tw-border-border tw-text-foreground tw-ring-ring" : "tw-border-[#c2cad8] tw-ring-blue-600",
        { "tw-rounded-md": rounded },
        className,
      )}
      {...props}
    >
      {children}

      <SelectPrimitive.Icon className={clsx("tw-absolute tw-right-0 tw-top-1/2 tw-inline-flex -tw-translate-y-1/2", "tw-items-center tw-pr-2")}>
        <ChevronDownIcon className="tw-size-5 tw-text-gray-400" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent({ className, children, position = "popper", ...props }: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        sideOffset={6}
        className={clsx(
          "tw-relative tw-w-full tw-px-0",
          "tw-border tw-border-solid tw-border-[#c2cad8] tw-shadow-md",
          "data-[state=open]:tw-animate-in",
          "motion-safe:data-[state=open]:tw-fade-in-0 motion-safe:data-[state=open]:tw-zoom-in-95",
          className,
        )}
        position={position}
        {...props}
      >
        <SelectPrimitive.Viewport className={clsx("tw-bg-white tw-p-1 tw-shadow-lg", "tw-h-[var(--radix-select-trigger-height)] tw-w-[var(--radix-select-trigger-width)]")}>
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      // `tw-bg-neutral-100` matches `--rd-accent` (245 245 245) without depending on that variable:
      // `SelectContent` portals this item out
      // via Radix (same as the Tooltip content elsewhere in the app) and doesn't re-apply
      // `[data-redesign-v1]` on the portaled node, so `--rd-accent` (scoped to that attribute) isn't
      // guaranteed to resolve at the portal's DOM position. The Tailwind color remains available there.
      className={clsx("tw-relative tw-cursor-default max-sm:tw-text-base", "focus-visible:tw-outline-none data-[highlighted]:tw-bg-neutral-100", "tw-py-1.5 tw-pl-8 tw-pr-2", className)}
      {...props}
    >
      <SelectPrimitive.ItemIndicator className="tw-absolute tw-left-2 tw-top-2 tw-size-3.5">
        <CheckIcon className="tw-size-4" />
      </SelectPrimitive.ItemIndicator>

      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}
